import React, { useEffect, useState } from "react";
import API_URL from "../../functions/config";

const UploadPayroll = () => {

    // =====================================================
    // STATES
    // =====================================================

    const [employeeId, setEmployeeId] = useState("");
    const [employees, setEmployees] = useState([]);

    const [month, setMonth] = useState(
        new Date().getMonth() + 1
    );

    const [year, setYear] = useState(
        new Date().getFullYear()
    );

    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [loadingPayroll, setLoadingPayroll] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false);

    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [success, setSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [payroll, setPayroll] = useState(null);
    const [payrolls, setPayrolls] = useState([]);


    // =====================================================
    // MONTHS
    // =====================================================

    const months = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" }
    ];


    // =====================================================
    // GET MONTH NAME
    // =====================================================

    const getMonthName = (monthNumber) => {

        const month = months.find(
            (item) =>
                Number(item.value) === Number(monthNumber)
        );

        return month ? month.label : "-";

    };


    // =====================================================
    // GET TOKEN
    // =====================================================

    const getToken = () => {

        const token = localStorage.getItem("token");

        if (!token) {
            return "";
        }

        // Handles both:
        // localStorage.setItem("token", token)
        // and
        // localStorage.setItem("token", JSON.stringify(token))

        try {

            const parsed = JSON.parse(token);

            return parsed || "";

        } catch (error) {

            return token;

        }

    };


    // =====================================================
    // GET USER
    // =====================================================

    const getUser = () => {

        const userData = localStorage.getItem("user");

        if (!userData) {
            return null;
        }

        try {

            return JSON.parse(userData);

        } catch (error) {

            return null;

        }

    };


    // =====================================================
    // CLEAR MESSAGES
    // =====================================================

    const clearMessages = () => {

        setError(false);
        setErrorMessage("");

        setSuccess(false);
        setSuccessMessage("");

    };


    // =====================================================
    // GET EMPLOYEES
    // =====================================================

    useEffect(() => {

        const getEmployees = async () => {

            try {

                setLoadingEmployees(true);

                const user = getUser();
                const token = getToken();

                if (!user || !user._id) {

                    setError(true);

                    setErrorMessage(
                        "User session not found. Please login again."
                    );

                    return;

                }


                const companyId = user._id;


                const response = await fetch(
                    `${API_URL}/employees/${companyId}`,
                    {
                        method: "GET",

                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    }
                );


                const result = await response.json();

                console.log(
                    "EMPLOYEE API RESPONSE:",
                    result
                );


                if (!response.ok) {

                    setError(true);

                    setErrorMessage(
                        result.message ||
                        "Failed to load employees."
                    );

                    return;

                }


                // =================================================
                // NORMALIZE EMPLOYEE RESPONSE
                // =================================================

                let employeeList = [];


                // API:
                // [ ... ]

                if (Array.isArray(result)) {

                    employeeList = result;

                }


                // API:
                // { employees: [ ... ] }

                else if (
                    result &&
                    Array.isArray(result.employees)
                ) {

                    employeeList = result.employees;

                }


                // API:
                // { data: [ ... ] }

                else if (
                    result &&
                    Array.isArray(result.data)
                ) {

                    employeeList = result.data;

                }


                // API:
                // { result: [ ... ] }

                else if (
                    result &&
                    Array.isArray(result.result)
                ) {

                    employeeList = result.result;

                }


                // Safety

                if (!Array.isArray(employeeList)) {

                    employeeList = [];

                }


                setEmployees(employeeList);


            } catch (error) {

                console.error(
                    "GET EMPLOYEES ERROR:",
                    error
                );

                setError(true);

                setErrorMessage(
                    "Unable to connect to server."
                );

            } finally {

                setLoadingEmployees(false);

            }

        };


        getEmployees();

    }, []);


    // =====================================================
    // GET ALL PAYROLL HISTORY
    // =====================================================

    const getPayrollHistory = async () => {

        try {

            setLoadingPayroll(true);

            const user = getUser();
            const token = getToken();


            if (!user || !user._id) {

                setError(true);

                setErrorMessage(
                    "User session not found. Please login again."
                );

                return;

            }


            const companyId = user._id;


            const response = await fetch(
                `${API_URL}/payroll?companyId=${encodeURIComponent(
                    companyId
                )}`,
                {
                    method: "GET",

                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );


            const result = await response.json();

            console.log(
                "PAYROLL HISTORY RESPONSE:",
                result
            );


            if (!response.ok) {

                setError(true);

                setErrorMessage(
                    result.message ||
                    "Failed to load payroll history."
                );

                return;

            }


            // =================================================
            // NORMALIZE PAYROLL RESPONSE
            // =================================================

            let payrollList = [];


            // [ ... ]

            if (Array.isArray(result)) {

                payrollList = result;

            }


            // { payrolls: [ ... ] }

            else if (
                result &&
                Array.isArray(result.payrolls)
            ) {

                payrollList = result.payrolls;

            }


            // { payroll: [ ... ] }

            else if (
                result &&
                Array.isArray(result.payroll)
            ) {

                payrollList = result.payroll;

            }


            // { data: [ ... ] }

            else if (
                result &&
                Array.isArray(result.data)
            ) {

                payrollList = result.data;

            }


            setPayrolls(
                Array.isArray(payrollList)
                    ? payrollList
                    : []
            );


        } catch (error) {

            console.error(
                "GET PAYROLL HISTORY ERROR:",
                error
            );

            setError(true);

            setErrorMessage(
                "Unable to load payroll history."
            );

        } finally {

            setLoadingPayroll(false);

        }

    };


    // =====================================================
    // LOAD PAYROLL HISTORY
    // =====================================================

    useEffect(() => {

        getPayrollHistory();

    }, []);


    // =====================================================
    // VIEW PAYROLL
    // =====================================================

    const viewPayroll = async () => {

        clearMessages();

        setPayroll(null);


        if (!employeeId) {

            setError(true);

            setErrorMessage(
                "Please select an employee."
            );

            return;

        }


        try {

            setLoadingPayroll(true);

            const user = getUser();
            const token = getToken();


            if (!user || !user._id) {

                setError(true);

                setErrorMessage(
                    "User session not found. Please login again."
                );

                return;

            }


            const companyId = user._id;


            const url =
                `${API_URL}/payroll` +
                `?companyId=${encodeURIComponent(companyId)}` +
                `&employeeId=${encodeURIComponent(employeeId)}` +
                `&month=${Number(month)}` +
                `&year=${Number(year)}`;


            console.log(
                "VIEW PAYROLL URL:",
                url
            );


            const response = await fetch(
                url,
                {
                    method: "GET",

                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );


            const result = await response.json();

            console.log(
                "VIEW PAYROLL RESPONSE:",
                result
            );


            if (!response.ok) {

                setError(true);

                setErrorMessage(
                    result.message ||
                    "Failed to load payroll."
                );

                return;

            }


            let payrollList = [];


            // API returns:
            // { payrolls: [...] }

            if (
                result &&
                Array.isArray(result.payrolls)
            ) {

                payrollList = result.payrolls;

            }


            // API returns:
            // { payroll: [...] }

            else if (
                result &&
                Array.isArray(result.payroll)
            ) {

                payrollList = result.payroll;

            }


            // API returns:
            // { data: [...] }

            else if (
                result &&
                Array.isArray(result.data)
            ) {

                payrollList = result.data;

            }


            // API directly returns array

            else if (Array.isArray(result)) {

                payrollList = result;

            }


            // =================================================
            // PAYROLL FOUND
            // =================================================

            if (payrollList.length > 0) {

                setPayroll(
                    payrollList[0]
                );

                return;

            }


            // =================================================
            // API MAY RETURN SINGLE OBJECT
            // =================================================

            if (
                result &&
                result.payroll &&
                !Array.isArray(result.payroll)
            ) {

                setPayroll(
                    result.payroll
                );

                return;

            }


            if (
                result &&
                result.data &&
                !Array.isArray(result.data)
            ) {

                setPayroll(
                    result.data
                );

                return;

            }


            // =================================================
            // NOT FOUND
            // =================================================

            setError(true);

            setErrorMessage(
                `No payroll found for ${getMonthName(
                    month
                )} ${year}.`
            );


        } catch (error) {

            console.error(
                "VIEW PAYROLL ERROR:",
                error
            );

            setError(true);

            setErrorMessage(
                "Unable to connect to server."
            );

        } finally {

            setLoadingPayroll(false);

        }

    };


    // =====================================================
    // GENERATE PAYROLL
    // =====================================================

    const generatePayroll = async () => {

        clearMessages();

        setPayroll(null);


        if (!employeeId) {

            setError(true);

            setErrorMessage(
                "Please select an employee."
            );

            return;

        }


        try {

            setLoadingGenerate(true);

            const token = getToken();


            const response = await fetch(
                `${API_URL}/generate-payroll`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },

                    body: JSON.stringify({

                        employeeId: employeeId,

                        month: Number(month),

                        year: Number(year)

                    })
                }
            );


            const result = await response.json();

            console.log(
                "GENERATE PAYROLL RESPONSE:",
                result
            );


            if (!response.ok) {

                setError(true);

                setErrorMessage(
                    result.message ||
                    "Failed to generate payroll."
                );

                return;

            }


            setSuccess(true);

            setSuccessMessage(
                result.message ||
                "Payroll generated successfully."
            );


            // =================================================
            // GET GENERATED PAYROLL
            // =================================================

            if (
                result.payroll &&
                !Array.isArray(result.payroll)
            ) {

                setPayroll(
                    result.payroll
                );

            }

            else if (
                result.data &&
                !Array.isArray(result.data)
            ) {

                setPayroll(
                    result.data
                );

            }

            else {

                // Fetch it from payroll API

                await loadSelectedPayrollAfterGenerate();

            }


            // Refresh history

            await getPayrollHistory();


        } catch (error) {

            console.error(
                "GENERATE PAYROLL ERROR:",
                error
            );

            setError(true);

            setErrorMessage(
                "Unable to connect to server."
            );

        } finally {

            setLoadingGenerate(false);

        }

    };


    // =====================================================
    // LOAD SELECTED PAYROLL AFTER GENERATE
    // =====================================================

    const loadSelectedPayrollAfterGenerate = async () => {

        try {

            const user = getUser();
            const token = getToken();


            if (!user || !user._id) {
                return;
            }


            const companyId = user._id;


            const response = await fetch(
                `${API_URL}/payroll` +
                `?companyId=${encodeURIComponent(companyId)}` +
                `&employeeId=${encodeURIComponent(employeeId)}` +
                `&month=${Number(month)}` +
                `&year=${Number(year)}`,
                {
                    method: "GET",

                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );


            const result = await response.json();


            if (!response.ok) {
                return;
            }


            let payrollList = [];


            if (
                result &&
                Array.isArray(result.payrolls)
            ) {

                payrollList = result.payrolls;

            }

            else if (
                result &&
                Array.isArray(result.payroll)
            ) {

                payrollList = result.payroll;

            }

            else if (
                result &&
                Array.isArray(result.data)
            ) {

                payrollList = result.data;

            }

            else if (
                Array.isArray(result)
            ) {

                payrollList = result;

            }


            if (payrollList.length > 0) {

                setPayroll(
                    payrollList[0]
                );

            }

        } catch (error) {

            console.error(
                "LOAD GENERATED PAYROLL ERROR:",
                error
            );

        }

    };


    // =====================================================
    // SELECT PAYROLL FROM TABLE
    // =====================================================

    const selectPayrollFromTable = (item) => {

        clearMessages();


        setPayroll(item);


        // employeeId can be:
        //
        // employeeId: "123"
        //
        // OR
        //
        // employeeId: {
        //    _id: "123",
        //    name: "John"
        // }

        const selectedEmployeeId =
            item.employeeId &&
            typeof item.employeeId === "object"
                ? item.employeeId._id
                : item.employeeId;


        if (selectedEmployeeId) {

            setEmployeeId(
                String(selectedEmployeeId)
            );

        }


        if (item.month) {

            setMonth(
                Number(item.month)
            );

        }


        if (item.year) {

            setYear(
                Number(item.year)
            );

        }


        // Scroll to payroll details

        setTimeout(() => {

            const element =
                document.getElementById(
                    "payroll-details"
                );

            if (element) {

                element.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });

            }

        }, 100);

    };


    // =====================================================
    // EMPLOYEE NAME
    // =====================================================

    const getEmployeeName = (item) => {

        if (!item) {
            return "-";
        }


        // Populated employeeId

        if (
            item.employeeId &&
            typeof item.employeeId === "object"
        ) {

            return (
                item.employeeId.name ||
                item.employeeId.fullName ||
                item.employeeId.employeeName ||
                item.employeeId.empid ||
                "-"
            );

        }


        // Direct employeeName

        if (item.employeeName) {

            return item.employeeName;

        }


        // Direct name

        if (item.name) {

            return item.name;

        }


        // Find employee from loaded employees

        if (item.employeeId) {

            const employee =
                employees.find(
                    (emp) =>
                        String(emp._id) ===
                        String(item.employeeId)
                );


            if (employee) {

                return (
                    employee.name ||
                    employee.fullName ||
                    employee.employeeName ||
                    employee.empid ||
                    "-"
                );

            }

        }


        return "-";

    };


    // =====================================================
    // SAFE VALUE
    // =====================================================

    const safeValue = (
        value,
        fallback = "-"
    ) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {

            return fallback;

        }

        return value;

    };


    // =====================================================
    // RENDER
    // =====================================================



    const deletePayroll = async (payrollId) => {

    const confirmDelete = window.confirm(
        "Are you sure you want to delete this payroll?"
    );

    if (!confirmDelete) {
        return;
    }


    try {

        clearMessages();

        const token = getToken();


        const response = await fetch(
            `${API_URL}/payroll/${payrollId}`,
            {
                method: "DELETE",

                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }
        );


        const result = await response.json();


        if (!response.ok) {

            setError(true);

            setErrorMessage(
                result.message ||
                "Failed to delete payroll."
            );

            return;

        }


        // Remove from current table immediately

        setPayrolls((previous) =>
            previous.filter(
                (item) =>
                    item._id !== payrollId
            )
        );


        // If deleted payroll is currently displayed

        if (
            payroll &&
            payroll._id === payrollId
        ) {

            setPayroll(null);

        }


        setSuccess(true);

        setSuccessMessage(
            result.message ||
            "Payroll deleted successfully."
        );


    } catch (error) {

        console.error(
            "DELETE PAYROLL ERROR:",
            error
        );

        setError(true);

        setErrorMessage(
            "Unable to connect to server."
        );

    }

};



    return (

        <div className="content">

            <main className="page-content p-4">


                {/* =================================================
                    PAYROLL FILTER / GENERATE
                ================================================= */}

                <div className="card">

                    <div className="card-header">

                        <h4 className="mb-0">
                            Payroll
                        </h4>

                    </div>


                    <div className="card-body">


                        {/* =================================================
                            ERROR
                        ================================================= */}

                        {error && (

                            <div className="alert alert-danger">

                                {errorMessage}

                            </div>

                        )}


                        {/* =================================================
                            SUCCESS
                        ================================================= */}

                        {success && (

                            <div className="alert alert-success">

                                {successMessage}

                            </div>

                        )}


                        <div className="row">


                            {/* =================================================
                                EMPLOYEE
                            ================================================= */}

                            <div className="col-lg-4 mb-3">

                                <label className="form-label">
                                    Employee
                                </label>


                                <select
                                    className="form-control"
                                    value={employeeId}
                                    onChange={(e) => {

                                        setEmployeeId(
                                            e.target.value
                                        );

                                        setPayroll(null);

                                        clearMessages();

                                    }}
                                    disabled={
                                        loadingEmployees
                                    }
                                >

                                    <option value="">

                                        {loadingEmployees
                                            ? "Loading Employees..."
                                            : "Select Employee"
                                        }

                                    </option>


                                    {Array.isArray(employees) &&
                                        employees.map(
                                            (employee) => (

                                                <option
                                                    key={
                                                        employee._id
                                                    }
                                                    value={
                                                        employee._id
                                                    }
                                                >

                                                    {employee.name ||
                                                        employee.fullName ||
                                                        employee.employeeName ||
                                                        "Employee"
                                                    }

                                                    {employee.empid
                                                        ? ` - ${employee.empid}`
                                                        : ""
                                                    }

                                                </option>

                                            )
                                        )}

                                </select>

                            </div>


                            {/* =================================================
                                MONTH
                            ================================================= */}

                            <div className="col-lg-3 mb-3">

                                <label className="form-label">
                                    Month
                                </label>


                                <select
                                    className="form-control"
                                    value={month}
                                    onChange={(e) => {

                                        setMonth(
                                            Number(
                                                e.target.value
                                            )
                                        );

                                        setPayroll(null);

                                        clearMessages();

                                    }}
                                >

                                    {months.map(
                                        (item) => (

                                            <option
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >

                                                {item.label}

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                            {/* =================================================
                                YEAR
                            ================================================= */}

                            <div className="col-lg-2 mb-3">

                                <label className="form-label">
                                    Year
                                </label>


                                <select
                                    className="form-control"
                                    value={year}
                                    onChange={(e) => {

                                        setYear(
                                            Number(
                                                e.target.value
                                            )
                                        );

                                        setPayroll(null);

                                        clearMessages();

                                    }}
                                >

                                    <option value={2026}>
                                        2026
                                    </option>

                                    <option value={2027}>
                                        2027
                                    </option>

                                    <option value={2028}>
                                        2028
                                    </option>

                                    <option value={2029}>
                                        2029
                                    </option>

                                    <option value={2030}>
                                        2030
                                    </option>

                                </select>

                            </div>


                            {/* =================================================
                                BUTTONS
                            ================================================= */}

                            <div className="col-lg-3 mb-3">

                                <label className="form-label">
                                    &nbsp;
                                </label>


                                <div className="d-flex gap-2">

                                    <button
                                        type="button"
                                        className="btn btn-info"
                                        onClick={
                                            viewPayroll
                                        }
                                        disabled={
                                            loadingPayroll
                                        }
                                    >

                                        {loadingPayroll
                                            ? "Loading..."
                                            : "View Payroll"
                                        }

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={
                                            generatePayroll
                                        }
                                        disabled={
                                            loadingGenerate
                                        }
                                    >

                                        {loadingGenerate
                                            ? "Generating..."
                                            : "Generate"
                                        }

                                    </button>

                                </div>

                            </div>


                        </div>


                    </div>

                </div>


                {/* =================================================
                    PAYROLL DETAILS
                ================================================= */}

                {payroll && (

                    <div
                        id="payroll-details"
                        className="card mt-4"
                    >

                        <div className="card-header">

                            <div className="d-flex justify-content-between align-items-center">

                                <h4 className="mb-0">
                                    Payroll Details
                                </h4>


                                <span className="badge bg-primary">

                                    {getMonthName(
                                        payroll.month ||
                                        month
                                    )}

                                    {" "}

                                    {payroll.year ||
                                        year
                                    }

                                </span>

                            </div>

                        </div>


                        <div className="card-body">


                            <div className="row">


                                {/* Employee */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Employee
                                    </strong>

                                    <div className="mt-1">

                                        {getEmployeeName(
                                            payroll
                                        )}

                                    </div>

                                </div>


                                {/* Salary Type */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Salary Type
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.salaryType
                                        )}

                                    </div>

                                </div>


                                {/* Salary */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Salary Rate
                                    </strong>

                                    <div className="mt-1">

                                        ₹
                                        {safeValue(
                                            payroll.salaryRate,
                                            "0"
                                        )}

                                    </div>

                                </div>


                                {/* Working Days */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Working Days
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.workingDays,
                                            "0"
                                        )}

                                    </div>

                                </div>


                                {/* Present */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Present Days
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.presentDays,
                                            "0"
                                        )}

                                    </div>

                                </div>


                                {/* Half Days */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Half Days
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.halfDays,
                                            "0"
                                        )}

                                    </div>

                                </div>


                                {/* Absent */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Absent Days
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.absentDays,
                                            "0"
                                        )}

                                    </div>

                                </div>


                                {/* Worked */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Worked Time
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.workedTime
                                        )}

                                    </div>

                                </div>


                                {/* Required */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Required Time
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.requiredTime
                                        )}

                                    </div>

                                </div>


                                {/* Overtime */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Overtime
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.overtime
                                        )}

                                    </div>

                                </div>


                                {/* Status */}

                                <div className="col-lg-3 mb-4">

                                    <strong>
                                        Status
                                    </strong>

                                    <div className="mt-1">

                                        {safeValue(
                                            payroll.status
                                        )}

                                    </div>

                                </div>


                            </div>


                            <hr />


                            {/* =================================================
                                SALARY SUMMARY
                            ================================================= */}

                            <div className="row">


                                <div className="col-lg-4">

                                    <h6>
                                        Gross Salary
                                    </h6>

                                    <h4>

                                        ₹
                                        {safeValue(
                                            payroll.grossSalary,
                                            "0"
                                        )}

                                    </h4>

                                </div>


                                <div className="col-lg-4">

                                    <h6>
                                        Deductions
                                    </h6>

                                    <h4>

                                        ₹
                                        {safeValue(
                                            payroll.deductions,
                                            "0"
                                        )}

                                    </h4>

                                </div>


                                <div className="col-lg-4">

                                    <h6>
                                        Net Salary
                                    </h6>

                                    <h3 className="text-success">

                                        ₹
                                        {safeValue(
                                            payroll.netSalary,
                                            "0"
                                        )}

                                    </h3>

                                </div>


                            </div>


                        </div>

                    </div>

                )}


                {/* =================================================
                    PAYROLL HISTORY
                ================================================= */}

                <div className="card mt-4">


                    <div className="card-header">

                        <div className="d-flex justify-content-between align-items-center">

                            <h4 className="mb-0">
                                Old Payroll
                            </h4>


                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={
                                    getPayrollHistory
                                }
                                disabled={
                                    loadingPayroll
                                }
                            >

                                {loadingPayroll
                                    ? "Loading..."
                                    : "Refresh"
                                }

                            </button>

                        </div>

                    </div>


                    <div className="card-body p-0">


                        {loadingPayroll ? (

                            <div className="text-center p-4">

                                <div
                                    className="spinner-border"
                                    role="status"
                                />

                                <div className="mt-2">
                                    Loading payroll...
                                </div>

                            </div>

                        ) : payrolls.length === 0 ? (

                            <div className="text-center p-4">

                                <div className="text-muted">
                                    No payroll records found.
                                </div>

                            </div>

                        ) : (

                            <div
                                className="table-responsive"
                                style={{
                                    maxHeight: "650px",
                                    overflowY: "auto"
                                }}
                            >

                                <table className="table table-bordered table-hover mb-0">

                                    <thead
                                        className="table-light"
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 2
                                        }}
                                    >

                                        <tr>

                                            <th>
                                                #
                                            </th>

                                            <th>
                                                Employee
                                            </th>

                                            <th>
                                                Date
                                            </th>

                                           

                                            <th>
                                                Salary
                                            </th>

                                            <th>
                                                Working Days
                                            </th>

                                            <th>
                                                Present
                                            </th>

                                            <th>
                                                Half Days
                                            </th>

                                            <th>
                                                Absent
                                            </th>

                                            <th>
                                                Worked Time
                                            </th>

                                         

                                            <th>
                                                Overtime
                                            </th>

                                            <th>
                                                Gross
                                            </th>

                                            <th>
                                                Deductions
                                            </th>

                                            <th>
                                                Net Salary
                                            </th>

                                            <th>
                                                Status
                                            </th>

                                            <th>
                                                Action
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {payrolls.map(
                                            (item, index) => (

                                                <tr
                                                    key={
                                                        item._id ||
                                                        index
                                                    }
                                                >

                                                    <td>
                                                        {index + 1}
                                                    </td>


                                                    <td>

                                                        {getEmployeeName(
                                                            item
                                                        )}

                                                    </td>


                                                    <td>

                                                        {getMonthName(
                                                            item.month
                                                        )+', '}

                                                          {safeValue(
                                                            item.year
                                                        )}

                                                    </td>


                                                  


                                                    <td>

                                                        ₹
                                                        {safeValue(
                                                            item.salaryRate,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        {safeValue(
                                                            item.workingDays,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        {safeValue(
                                                            item.presentDays,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        {safeValue(
                                                            item.halfDays,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        {safeValue(
                                                            item.absentDays,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        {safeValue(
                                                            item.workedTime
                                                        )}

                                                    </td>


                                                  


                                                    <td>

                                                        {safeValue(
                                                            item.overtime
                                                        )}

                                                    </td>


                                                    <td>

                                                        ₹
                                                        {safeValue(
                                                            item.grossSalary,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        ₹
                                                        {safeValue(
                                                            item.deductions,
                                                            "0"
                                                        )}

                                                    </td>


                                                    <td>

                                                        <strong className="text-success">

                                                            ₹
                                                            {safeValue(
                                                                item.netSalary,
                                                                "0"
                                                            )}

                                                        </strong>

                                                    </td>


                                                    <td>

                                                        <span
                                                            className={
                                                                item.status ===
                                                                "Paid"
                                                                    ? "badge bg-success"
                                                                    : item.status ===
                                                                      "Approved"
                                                                    ? "badge bg-info"
                                                                    : "badge bg-secondary"
                                                            }
                                                        >

                                                            {safeValue(
                                                                item.status
                                                            )}

                                                        </span>

                                                    </td>


                                                    <td>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() =>
                                                                selectPayrollFromTable(
                                                                    item
                                                                )
                                                            }
                                                        >

                                                            View

                                                        </button>


                                        

    <button
        type="button"
        className="btn btn-sm btn-danger"
        onClick={() =>
            deletePayroll(item._id)
        }
    >
        Delete
    </button>

                                                    </td>


                                                </tr>

                                            )
                                        )}

                                    </tbody>

                                </table>

                            </div>

                        )}

                    </div>

                </div>


            </main>

        </div>

    );

};

export default UploadPayroll;